import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ArrowRight, Zap, Target, TrendingUp, Users, Clock, DollarSign } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { updateMetaTags } from '@/utils/seoUtils';

const PaidAdsService = () => {
  const { currentLanguage } = useLanguage();
  const { city } = useParams<{ city?: string }>();

  useEffect(() => {
    const title = {
      de: `Paid Ads Management ${city ? `in ${city}` : 'Schweiz'} | Future Level`,
      en: `Paid Ads Management ${city ? `in ${city}` : 'Switzerland'} | Future Level`,
      fr: `Gestion Publicités Payantes ${city ? `à ${city}` : 'Suisse'} | Future Level`,
      it: `Gestione Annunci a Pagamento ${city ? `a ${city}` : 'Svizzera'} | Future Level`
    };

    const description = {
      de: `Professionelle Google Ads, Meta Ads, LinkedIn & TikTok Kampagnen für Schweizer Unternehmen. ROI-optimiert, datengetrieben, transparent.`,
      en: `Professional Google Ads, Meta Ads, LinkedIn & TikTok campaigns for Swiss businesses. ROI-optimized, data-driven, transparent.`,
      fr: `Campagnes Google Ads, Meta Ads, LinkedIn & TikTok professionnelles pour entreprises suisses. Optimisées ROI, pilotées données, transparentes.`,
      it: `Campagne Google Ads, Meta Ads, LinkedIn & TikTok professionali per aziende svizzere. Ottimizzate ROI, guidate dai dati, trasparenti.`
    };

    updateMetaTags({
      title: title[currentLanguage],
      description: description[currentLanguage],
      canonicalUrl: `https://futurelevel.ch/${currentLanguage}/paid-ads${city ? `/${city}` : ''}/`,
      language: currentLanguage
    });
  }, [currentLanguage, city]);

  const platforms = [
    {
      icon: Target,
      name: 'Google Ads',
      description: {
        de: 'Search, Shopping, Display & YouTube Ads für maximale Reichweite',
        en: 'Search, Shopping, Display & YouTube Ads for maximum reach',
        fr: 'Annonces Search, Shopping, Display & YouTube pour portée maximale',
        it: 'Annunci Search, Shopping, Display & YouTube per portata massima'
      },
      benefits: ['High Intent Traffic', 'Local Targeting', 'Immediate Results'],
      pricing: 'CHF 800-2,500/Monat'
    },
    {
      icon: Users,
      name: 'Meta Ads',
      description: {
        de: 'Facebook & Instagram Kampagnen für Brand Awareness und Conversions',
        en: 'Facebook & Instagram campaigns for brand awareness and conversions',
        fr: 'Campagnes Facebook & Instagram pour notoriété marque et conversions',
        it: 'Campagne Facebook & Instagram per awareness del brand e conversioni'
      },
      benefits: ['Precise Targeting', 'Visual Content', 'Social Proof'],
      pricing: 'CHF 600-2,000/Monat'
    },
    {
      icon: TrendingUp,
      name: 'LinkedIn Ads',
      description: {
        de: 'B2B-fokussierte Kampagnen für Entscheidungsträger und Fachkräfte',
        en: 'B2B-focused campaigns targeting decision makers and professionals',
        fr: 'Campagnes B2B ciblant décideurs et professionnels',
        it: 'Campagne B2B mirate a decisori e professionisti'
      },
      benefits: ['B2B Targeting', 'Professional Context', 'Lead Generation'],
      pricing: 'CHF 1,000-3,000/Monat'
    },
    {
      icon: Zap,
      name: 'TikTok Ads',
      description: {
        de: 'Viral Marketing für jüngere Zielgruppen und Trendprodukte',
        en: 'Viral marketing for younger audiences and trending products',
        fr: 'Marketing viral pour audiences jeunes et produits tendance',
        it: 'Marketing virale per pubblico giovane e prodotti di tendenza'
      },
      benefits: ['Young Demographics', 'Viral Potential', 'Creative Freedom'],
      pricing: 'CHF 500-1,500/Monat'
    }
  ];

  const services = [
    {
      title: {
        de: 'Kampagnen-Setup',
        en: 'Campaign Setup',
        fr: 'Configuration Campagnes',
        it: 'Configurazione Campagne'
      },
      description: {
        de: 'Strategische Planung, Zielgruppen-Definition, Creative Development',
        en: 'Strategic planning, audience definition, creative development',
        fr: 'Planification stratégique, définition audiences, développement créatif',
        it: 'Pianificazione strategica, definizione audience, sviluppo creativo'
      },
      included: ['Audience Research', 'Competitive Analysis', 'Landing Page Audit', 'KPI Definition']
    },
    {
      title: {
        de: 'Laufende Optimierung',
        en: 'Ongoing Optimization',
        fr: 'Optimisation Continue',
        it: 'Ottimizzazione Continua'
      },
      description: {
        de: 'Tägliches Monitoring, A/B Testing, Bid Management, Performance-Analyse',
        en: 'Daily monitoring, A/B testing, bid management, performance analysis',
        fr: 'Surveillance quotidienne, tests A/B, gestion enchères, analyse performance',
        it: 'Monitoraggio quotidiano, test A/B, gestione offerte, analisi performance'
      },
      included: ['Daily Optimizations', 'Creative Testing', 'Budget Management', 'Conversion Tracking']
    },
    {
      title: {
        de: 'Reporting & Insights',
        en: 'Reporting & Insights',
        fr: 'Rapports & Insights',
        it: 'Report & Insights'
      },
      description: {
        de: 'Wöchentliche Reports, ROI-Analyse, Handlungsempfehlungen',
        en: 'Weekly reports, ROI analysis, actionable recommendations',
        fr: 'Rapports hebdomadaires, analyse ROI, recommandations actionnables',
        it: 'Report settimanali, analisi ROI, raccomandazioni azionabili'
      },
      included: ['Performance Reports', 'ROI Tracking', 'Market Insights', 'Strategy Updates']
    }
  ];

  const results = [
    {
      metric: '+340%',
      label: {
        de: 'Durchschnittlicher ROAS',
        en: 'Average ROAS',
        fr: 'ROAS Moyen',
        it: 'ROAS Medio'
      }
    },
    {
      metric: '-65%',
      label: {
        de: 'Cost per Acquisition',
        en: 'Cost per Acquisition',
        fr: 'Coût par Acquisition',
        it: 'Costo per Acquisizione'
      }
    },
    {
      metric: '+280%',
      label: {
        de: 'Lead Quality Score',
        en: 'Lead Quality Score',
        fr: 'Score Qualité Leads',
        it: 'Punteggio Qualità Lead'
      }
    },
    {
      metric: '95%',
      label: {
        de: 'Client Retention Rate',
        en: 'Client Retention Rate',
        fr: 'Taux Rétention Client',
        it: 'Tasso Ritenzione Cliente'
      }
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-background to-background/80 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-background to-background"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="outline" className="mb-4">
                <Target className="w-4 h-4 mr-2" />
                {currentLanguage === 'de' ? 'Paid Advertising' : 
                 currentLanguage === 'fr' ? 'Publicité Payante' :
                 currentLanguage === 'it' ? 'Pubblicità a Pagamento' : 'Paid Advertising'}
              </Badge>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
                {currentLanguage === 'de' ? 'ROI-optimierte Paid Ads für Schweizer Unternehmen' :
                 currentLanguage === 'fr' ? 'Publicités Payantes Optimisées ROI pour Entreprises Suisses' :
                 currentLanguage === 'it' ? 'Annunci a Pagamento Ottimizzati ROI per Aziende Svizzere' :
                 'ROI-Optimized Paid Ads for Swiss Businesses'}
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
                {currentLanguage === 'de' ? 'Google Ads, Meta Ads, LinkedIn & TikTok - Transparente Performance, messbare Resultate, maximaler ROI' :
                 currentLanguage === 'fr' ? 'Google Ads, Meta Ads, LinkedIn & TikTok - Performance transparente, résultats mesurables, ROI maximal' :
                 currentLanguage === 'it' ? 'Google Ads, Meta Ads, LinkedIn & TikTok - Performance trasparente, risultati misurabili, ROI massimo' :
                 'Google Ads, Meta Ads, LinkedIn & TikTok - Transparent performance, measurable results, maximum ROI'}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button size="lg" className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary">
                  {currentLanguage === 'de' ? 'Kostenlose Ads-Analyse' :
                   currentLanguage === 'fr' ? 'Analyse Gratuite Pubs' :
                   currentLanguage === 'it' ? 'Analisi Gratuita Annunci' :
                   'Free Ads Analysis'}
                  <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button variant="outline" size="lg">
                  {currentLanguage === 'de' ? 'Erfolgsgeschichten' :
                   currentLanguage === 'fr' ? 'Histoires de Succès' :
                   currentLanguage === 'it' ? 'Storie di Successo' :
                   'Success Stories'}
                </Button>
              </div>

              {/* Results Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                {results.map((result, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl font-bold text-primary">{result.metric}</div>
                    <div className="text-sm text-muted-foreground">
                      {result.label[currentLanguage]}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Platforms Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'Unsere Advertising Plattformen' :
                 currentLanguage === 'fr' ? 'Nos Plateformes Publicitaires' :
                 currentLanguage === 'it' ? 'Le Nostre Piattaforme Pubblicitarie' :
                 'Our Advertising Platforms'}
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                {platforms.map((platform, index) => {
                  const Icon = platform.icon;
                  return (
                    <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-start gap-4">
                        <Icon className="w-12 h-12 text-primary flex-shrink-0" />
                        <div className="flex-grow">
                          <h3 className="text-xl font-semibold mb-2">{platform.name}</h3>
                          <p className="text-muted-foreground mb-4">
                            {platform.description[currentLanguage]}
                          </p>
                          <div className="flex flex-wrap gap-2 mb-4">
                            {platform.benefits.map((benefit, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {benefit}
                              </Badge>
                            ))}
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-lg font-bold text-primary">
                              {platform.pricing}
                            </span>
                            <Button size="sm">
                              {currentLanguage === 'de' ? 'Mehr erfahren' :
                               currentLanguage === 'fr' ? 'En savoir plus' :
                               currentLanguage === 'it' ? 'Scopri di più' :
                               'Learn More'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Services Process */}
        <section className="py-16 bg-background/50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'Unser Ads Management Prozess' :
                 currentLanguage === 'fr' ? 'Notre Processus de Gestion Pubs' :
                 currentLanguage === 'it' ? 'Il Nostro Processo di Gestione Annunci' :
                 'Our Ads Management Process'}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {services.map((service, index) => (
                  <Card key={index} className="p-6">
                    <div className="text-center mb-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-xl font-bold text-primary">{index + 1}</span>
                      </div>
                      <h3 className="text-xl font-semibold mb-2">
                        {service.title[currentLanguage]}
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        {service.description[currentLanguage]}
                      </p>
                    </div>
                    <div className="space-y-2">
                      {service.included.map((item, idx) => (
                        <div key={idx} className="flex items-center text-sm">
                          <ArrowRight className="w-4 h-4 text-primary mr-2 flex-shrink-0" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'Warum Future Level für Paid Ads?' :
                 currentLanguage === 'fr' ? 'Pourquoi Future Level pour Publicités Payantes?' :
                 currentLanguage === 'it' ? 'Perché Future Level per Annunci a Pagamento?' :
                 'Why Future Level for Paid Ads?'}
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <Clock className="w-8 h-8 text-primary flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        {currentLanguage === 'de' ? 'Tägliche Optimierung' :
                         currentLanguage === 'fr' ? 'Optimisation Quotidienne' :
                         currentLanguage === 'it' ? 'Ottimizzazione Quotidiana' :
                         'Daily Optimization'}
                      </h3>
                      <p className="text-muted-foreground">
                        {currentLanguage === 'de' ? 'Kontinuierliche Überwachung und Anpassung für maximale Performance' :
                         currentLanguage === 'fr' ? 'Surveillance et ajustements continus pour performance maximale' :
                         currentLanguage === 'it' ? 'Monitoraggio e aggiustamenti continui per performance massima' :
                         'Continuous monitoring and adjustments for maximum performance'}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <DollarSign className="w-8 h-8 text-primary flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        {currentLanguage === 'de' ? 'Transparente Preisgestaltung' :
                         currentLanguage === 'fr' ? 'Prix Transparents' :
                         currentLanguage === 'it' ? 'Prezzi Trasparenti' :
                         'Transparent Pricing'}
                      </h3>
                      <p className="text-muted-foreground">
                        {currentLanguage === 'de' ? 'Keine versteckten Kosten, faire Preise, volle Kostenkontrolle' :
                         currentLanguage === 'fr' ? 'Pas de coûts cachés, prix équitables, contrôle total des coûts' :
                         currentLanguage === 'it' ? 'Nessun costo nascosto, prezzi equi, controllo totale dei costi' :
                         'No hidden costs, fair pricing, complete cost control'}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <TrendingUp className="w-8 h-8 text-primary flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        {currentLanguage === 'de' ? 'Datengetriebene Entscheidungen' :
                         currentLanguage === 'fr' ? 'Décisions Pilotées par Données' :
                         currentLanguage === 'it' ? 'Decisioni Guidate dai Dati' :
                         'Data-Driven Decisions'}
                      </h3>
                      <p className="text-muted-foreground">
                        {currentLanguage === 'de' ? 'Alle Entscheidungen basieren auf echten Daten und messbaren KPIs' :
                         currentLanguage === 'fr' ? 'Toutes décisions basées sur données réelles et KPIs mesurables' :
                         currentLanguage === 'it' ? 'Tutte le decisioni basate su dati reali e KPI misurabili' :
                         'All decisions based on real data and measurable KPIs'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-primary/10 to-accent/10 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-4">
                    {currentLanguage === 'de' ? 'Swiss Market Expertise' :
                     currentLanguage === 'fr' ? 'Expertise Marché Suisse' :
                     currentLanguage === 'it' ? 'Competenza Mercato Svizzero' :
                     'Swiss Market Expertise'}
                  </h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center">
                      <ArrowRight className="w-4 h-4 text-primary mr-2" />
                      {currentLanguage === 'de' ? 'Lokale Zielgruppen-Insights' :
                       currentLanguage === 'fr' ? 'Insights audiences locales' :
                       currentLanguage === 'it' ? 'Insights audience locali' :
                       'Local audience insights'}
                    </li>
                    <li className="flex items-center">
                      <ArrowRight className="w-4 h-4 text-primary mr-2" />
                      {currentLanguage === 'de' ? 'Mehrsprachige Kampagnen (DE/FR/IT)' :
                       currentLanguage === 'fr' ? 'Campagnes multilingues (DE/FR/IT)' :
                       currentLanguage === 'it' ? 'Campagne multilingue (DE/FR/IT)' :
                       'Multilingual campaigns (DE/FR/IT)'}
                    </li>
                    <li className="flex items-center">
                      <ArrowRight className="w-4 h-4 text-primary mr-2" />
                      {currentLanguage === 'de' ? 'Swiss Compliance & Privacy' :
                       currentLanguage === 'fr' ? 'Conformité et confidentialité suisses' :
                       currentLanguage === 'it' ? 'Conformità e privacy svizzere' :
                       'Swiss compliance & privacy'}
                    </li>
                    <li className="flex items-center">
                      <ArrowRight className="w-4 h-4 text-primary mr-2" />
                      {currentLanguage === 'de' ? 'Lokale Konkurrenzanalyse' :
                       currentLanguage === 'fr' ? 'Analyse concurrence locale' :
                       currentLanguage === 'it' ? 'Analisi concorrenza locale' :
                       'Local competitive analysis'}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">
              {currentLanguage === 'de' ? 'Bereit für profitable Werbekampagnen?' :
               currentLanguage === 'fr' ? 'Prêt pour des campagnes publicitaires rentables?' :
               currentLanguage === 'it' ? 'Pronti per campagne pubblicitarie redditizie?' :
               'Ready for Profitable Ad Campaigns?'}
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              {currentLanguage === 'de' ? 'Lassen Sie uns Ihre bestehenden Kampagnen analysieren und einen Plan für maximalen ROI entwickeln.' :
               currentLanguage === 'fr' ? 'Laissez-nous analyser vos campagnes existantes et développer un plan pour un ROI maximal.' :
               currentLanguage === 'it' ? 'Lasciaci analizzare le tue campagne esistenti e sviluppare un piano per il massimo ROI.' :
               'Let us analyze your existing campaigns and develop a plan for maximum ROI.'}
            </p>
            <Button size="lg" asChild className="bg-gradient-to-r from-primary to-primary/90">
              <a href={`/${currentLanguage}/kontakt/`}>
                {currentLanguage === 'de' ? 'Kostenlose Ads-Analyse' :
                 currentLanguage === 'fr' ? 'Analyse Gratuite' :
                 currentLanguage === 'it' ? 'Analisi Gratuita' :
                 'Free Analysis'}
                <ArrowRight className="ml-2" size={20} />
              </a>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PaidAdsService;